import axios from 'axios';
import React, { createContext, useEffect, useState } from 'react';
import { IPropsPosition } from '../utils/@types/wallet';
import { financeRoutes } from '../utils/RoutesApi';

export const ActivesContext = createContext({});

interface IPropsActivesProvider {
  children: React.ReactNode;
}
interface IStateActives {
  novoSumAcao: number;
  novoSumFII: number;
  positionAcao: IPropsPosition[];
  positionFII: IPropsPosition[];
  positionsAll: IPropsPosition[];
  sumAcao: number;
  sumsEFT: number;
  sumsFII: number;
  rentabilidade: IProfitability[];
}
interface IProfitability {
  referenceDate: string;
  value: number;
}

interface IPercentage {
  fii: number;
  action: number;
  rendaFixa: number;
  stocks: number;
  etfs: number;
  reits: number;
  crypto: number;
}

const STOCK_TYPES = ['stock', 'stocks'];
const ETF_TYPES = ['etf', 'etfs'];
const REIT_TYPES = ['reit', 'reits'];
const CRYPTO_TYPES = ['crypto', 'cripto'];
const INTL_TYPES = [
  ...STOCK_TYPES,
  ...ETF_TYPES,
  ...REIT_TYPES,
  ...CRYPTO_TYPES,
];

function sumByTypes(items: any[], types: string[]) {
  return items
    .filter(
      (it: any) =>
        types.includes(it?.productTypeName) && !it?.alocado_total
    )
    .reduce((acc: number, it: any) => acc + Number(it?.updateValue || 0), 0);
}

export const ActivesProvider = ({ children }: IPropsActivesProvider) => {
  //@ts-ignore
  const isShowActive = localStorage.getItem('isShowActive');
  const [actives, setActives] = useState<IStateActives>({
    novoSumAcao: 0,
    novoSumFII: 0,
    positionAcao: [],
    positionFII: [],
    positionsAll: [],
    sumAcao: 0,
    sumsEFT: 0,
    sumsFII: 0,
    rentabilidade: [],
  });
  const [percentage, setPercentage] = useState<IPercentage>({
    fii: 0,
    action: 0,
    rendaFixa: 0,
    stocks: 0,
    etfs: 0,
    reits: 0,
    crypto: 0,
  });
  const [showActive, setShowActive] = useState<boolean>(false);
  const [loadingActive, setLoadingActive] = useState<boolean>(false);

  useEffect(() => {
    if (!isShowActive) {
      localStorage.setItem('isShowActive', 'false');
    } else {
      setShowActive(isShowActive !== 'false');
    }
  }, []);

  useEffect(() => {
    getActives();
  }, [location.pathname]);

  async function getActives() {
    setLoadingActive(true);
    await axios
      .get(`${process.env.REACT_APP_API_URL}${financeRoutes.FINANCE_POSITION}`)
      .then(function (result) {
        setActives(result.data);

        let act = Number(result.data.sumAcao || 0);
        let fii = Number(result.data.sumsFII || 0);

        const stocksSum = sumByTypes(result.data.positionsAll, STOCK_TYPES);
        const etfsSum = sumByTypes(result.data.positionsAll, ETF_TYPES);
        const reitsSum = sumByTypes(result.data.positionsAll, REIT_TYPES);
        const cryptoSum = sumByTypes(result.data.positionsAll, CRYPTO_TYPES);

        // Renda fixa = tudo que sobra (não-acao, não-fii e não-internacional)
        const rf = result.data.positionsAll
          .filter(
            (item: any) =>
              !['acao', 'fii', ...INTL_TYPES].includes(item.productTypeName) &&
              !item.alocado_total
          )
          .reduce(
            (sum: number, item: any) => sum + Number(item.updateValue || 0),
            0
          );

        const total =
          act + fii + rf + stocksSum + etfsSum + reitsSum + cryptoSum;

        const pct = (value: number) =>
          total === 0 ? 0 : Number(((value / total) * 100).toFixed(2));

        const newPercentage: IPercentage = {
          action: pct(act),
          fii: pct(fii),
          rendaFixa: pct(rf),
          stocks: pct(stocksSum),
          etfs: pct(etfsSum),
          reits: pct(reitsSum),
          crypto: pct(cryptoSum),
        };

        setPercentage(newPercentage);
      })
      .catch(function (error) {});
    setLoadingActive(false);
  }

  return (
    <ActivesContext.Provider
      value={{
        actives,
        setActives,
        setPercentage,
        percentage,
        showActive,
        setShowActive,
        loadingActive,
      }}
    >
      {children}
    </ActivesContext.Provider>
  );
};
